import { useSortable } from '@dnd-kit/sortable';
import { useLocalization } from '@lib/localization';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { type ReportBlock } from '@store/api/reportDesignEndpoints';
import { ArrowDownIcon, ArrowUpIcon, CopyIcon, FileIcon, GripVerticalIcon, TrashIcon } from 'react-line-awesome';
import { definitionOf } from '../blockCatalog';
import BlockView from './BlockView';
import {
  blockFrameStyles, blockGrabStyles, blockLiftedStyles, blockSelectedStyles, blockToolbarStyles, dropLineBelowStyles,
  dropLineStyles, firstBlockToolbarStyles, flaggedStyles, gripStyles, toolbarNameStyles, toolbarRoomStyles,
} from './DesignCanvas.styles';
import { dragIds, splitListeners, type BlockPart } from './dragging';
import { type Print } from './print';
import { useReveal } from './useReveal';

/** What can be done to a block from its toolbar. Each is offered only where it is possible. */
export interface BlockFrameActions {
  select: () => void;
  /** One place up or down, from the edge of this page onto the next one that has room. Left out where there is no such place. */
  stepUp?: () => void;
  stepDown?: () => void;
  /** Left out on a page that is full. */
  duplicate?: () => void;
  remove: () => void;
}

interface Props {
  block: ReportBlock;
  pageId: string;
  index: number;
  /** How many blocks its page holds. */
  count: number;
  print: Print;
  /** On a navy page. */
  dark: boolean;
  canEdit: boolean;
  selected: boolean;
  /** An issue is about this block. */
  flagged: boolean;
  /** The first block of its page. */
  first: boolean;
  /** A drag would land beside this block: over it, or under it, which only the last block of a page is asked. */
  line?: 'above' | 'below';
  /** See `useReveal`. */
  shown?: number;
  actions: BlockFrameActions;
}

const stop = (event: React.SyntheticEvent) => event.stopPropagation();

/**
 * One block on its sheet: the block as it prints, and around it what the editor adds. A
 * click selects it. Whoever may edit picks it up by any part of it, and the selected block
 * carries a toolbar with a grip that is always to be seen, which is also what the keyboard
 * picks it up by, and with a button for everything a drag can do: a block is moved a place
 * at a time, across pages too, without one.
 *
 * It is a sortable item of its page for the drag context, and takes none of the preset's
 * movement: the block stays where it is while it is dragged (see `useBlockDrag`).
 *
 * A screen reader is told what kind of block it is and where on its page it stands, not what
 * it prints: a divider, a spacer and a picture print no word to go by, and a text would be
 * read out whole.
 */
const BlockFrame: React.FC<Props> = ({
  block, pageId, index, count, print, dark, canEdit, selected, flagged, first, line, shown, actions,
}) => {
  const { t } = useLocalization();
  const part: BlockPart = { kind: 'block', blockId: block.id, pageId, index };

  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging, node } = useSortable({
    id: dragIds.block(block.id),
    data: part,
    disabled: !canEdit,
    // What a screen reader calls the grip. The library's own word for it is English.
    attributes: { roleDescription: t('ReportDesign_Drag_Handle') },
  });
  const { pointer, keyboard } = splitListeners(listeners);

  useReveal(node, shown);

  const definition = definitionOf(block.type);
  const Icon = definition?.icon ?? FileIcon;
  const name = definition ? t(definition.label) : block.type;

  // `step` marks the two buttons that move the block a place: after a press the keyboard's
  // focus is put back on the one that was pressed, wherever the block stands by then (`useHandover`).
  const action = (title: string, icon: React.ReactNode, onClick: (() => void) | undefined, step?: 'up' | 'down') => (
    <Tooltip title={title}>
      <span>
        <IconButton size="small" aria-label={title} data-step={step} disabled={!onClick} onClick={onClick}>{icon}</IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box
      ref={setNodeRef}
      role="button"
      tabIndex={0}
      aria-label={t('ReportDesign_Drag_Place', [name, index + 1, count])}
      aria-current={selected || undefined}
      // Which block the frame is of, for the keyboard's focus to be handed on to it (`useHandover`).
      data-block={block.id}
      sx={[
        blockFrameStyles,
        canEdit && blockGrabStyles,
        flagged && flaggedStyles,
        selected && blockSelectedStyles,
        selected && canEdit && toolbarRoomStyles,
        isDragging && blockLiftedStyles,
      ]}
      onClick={(event) => {
        // Not the page under it.
        event.stopPropagation();
        actions.select();
      }}
      onKeyDown={(event) => {
        // Its own keys only: the grip inside it takes Space to pick the block up.
        if ((event.key === 'Enter' || event.key === ' ') && event.target === event.currentTarget) {
          // Space would scroll the canvas by a screen besides.
          event.preventDefault();
          actions.select();
        }
      }}
      {...pointer}
    >
      {line && <Box sx={line === 'below' ? [dropLineStyles, dropLineBelowStyles] : dropLineStyles} />}

      {selected && canEdit && (
        <Stack direction="row" sx={first ? [blockToolbarStyles, firstBlockToolbarStyles] : blockToolbarStyles} onClick={stop}>
          <Box
            component="span"
            ref={setActivatorNodeRef}
            className="drag-handle"
            title={t('ReportDesign_Canvas_Grip')}
            sx={gripStyles}
            {...attributes}
            {...keyboard}
          >
            <GripVerticalIcon />
          </Box>
          <Stack direction="row" sx={toolbarNameStyles}>
            <Icon />
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</Box>
          </Stack>
          {/* A press on a button is a press on the button, not the start of a drag. */}
          <Stack direction="row" onMouseDown={stop} onTouchStart={stop}>
            {action(t('Customization_Designer_MoveUp'), <ArrowUpIcon />, actions.stepUp, 'up')}
            {action(t('Customization_Designer_MoveDown'), <ArrowDownIcon />, actions.stepDown, 'down')}
            {action(t('Customization_Duplicate'), <CopyIcon />, actions.duplicate)}
            {action(t('Common_Delete'), <TrashIcon />, actions.remove)}
          </Stack>
        </Stack>
      )}

      <BlockView block={block} print={print} dark={dark} />
    </Box>
  );
};

export default BlockFrame;
